/* ═══════════════════════════════════════════════════════════════
   WList — compact row list body. Absorbs the report-widgets `Row`
   primitive (hover highlight, keyboard reachable, ≥32px hit area,
   DESIGN.md §4 Fitts floor). Rows lay out in a CSS grid
   (`repeat(auto-fill, minmax(LIST_MIN_COL_REM, 1fr))`) so a narrow
   widget stays one column and a wide one splits into 2/3+ — the grid's
   default row-major flow keeps DOM order == reading order (left→right,
   top→bottom) with no extra wiring. Slices to `max` rows; `onViewAll`
   renders the "View all N" footer in the body itself (one
   implementation every list widget can share instead of each widget
   wiring `WidgetFrame`'s `footer` prop by hand — that path still works
   for callers that prefer it). `footer` is a free slot for a "+ Add"
   row at the bottom of the body.

   `groups` (Team widget rebuild, 2026-10-02) is an alternate rendering
   mode for callers whose rows cluster into named sections (department,
   team, etc.) that should sit side by side as columns rather than flow
   as one flat row-grid: each group stacks its own rows vertically under
   a caller-supplied label, and the groups themselves lay out in the same
   auto-fill column grid so they reflow (never branch on width tiers).
   Mutually exclusive with `rows` — a caller passes one or the other.
   Unslicing: group rows are never capped by `max` (a roster has no
   "view more", unlike a flat list).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { cn } from '../../lib/utils';
import { rem } from '../../lib/rem';

export interface WListRow {
  id: string;
  lead?: React.ReactNode;
  primary: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
  trailing?: React.ReactNode;
  /** Keeps `trailing` visually quiet until the row is hovered OR a
   *  descendant has keyboard focus (never `display:none` — the control
   *  stays tab-reachable at all times). Opt-in per row so every existing
   *  caller's trailing slot keeps rendering exactly as before. */
  trailingReveal?: boolean;
}

export interface WListGroup {
  key: string;
  /** Caller-styled section label (e.g. `sectionLabel` from the project
   *  widgets' shared.tsx) — WList stays a kit primitive and doesn't know
   *  about page-level styles, so it renders whatever node it's given. */
  label: React.ReactNode;
  rows: WListRow[];
}

/** Minimum column width (rem) before the grid wraps to another column. */
export const LIST_MIN_COL_REM = 18;

export interface WListProps {
  /** Flat row mode. Omit when `groups` is supplied. */
  rows?: WListRow[];
  /** Grouped-column mode — see the file header comment. Omit when `rows`
   *  is supplied. */
  groups?: WListGroup[];
  /** Rows actually rendered. Default 8. Ignored in `groups` mode. */
  max?: number;
  /** Minimum column width (rem). Defaults to LIST_MIN_COL_REM. In `groups`
   *  mode this sizes the group columns instead of row columns. */
  minColRem?: number;
  /** Renders a "View all N" row under the grid when `rows.length` exceeds
   *  what's visible. `N` is always the full `rows.length`, independent of
   *  how many were sliced in. Ignored in `groups` mode. */
  onViewAll?: () => void;
  /** Free slot rendered under the grid (before the "View all" row) — e.g.
   *  a "+ Add" row. */
  footer?: React.ReactNode;
}

/** Pure slicing helper — first N rows, no side effects. */
export function sliceRows<T>(rows: T[], max = 8): T[] {
  return rows.slice(0, Math.max(0, max));
}

/** Footer link text for a "View all N" row — `total` is the caller's
 *  full count, independent of how many rows were fetched/rendered. */
export function viewAllLabel(total: number): string {
  return `View all ${total}`;
}

/** Pure column-count helper mirroring `repeat(auto-fill, minmax(minColRem,
 *  1fr))`: how many `minColRem`-wide columns fit in `widthRem`. Never
 *  fewer than 1. */
export function listColumnsFor(widthRem: number, minColRem: number = LIST_MIN_COL_REM): number {
  if (!(widthRem > 0) || !(minColRem > 0)) return 1;
  return Math.max(1, Math.floor(widthRem / minColRem));
}

function WListRowItem({ id, lead, primary, meta, onClick, trailing, trailingReveal }: WListRow) {
  const clickable = !!onClick;
  return (
    <div
      data-wlist-row={id}
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={cn(
        // `group` scopes `trailingReveal`'s group-hover/group-focus-within
        // below — inert on its own, so every pre-existing row keeps its
        // exact prior appearance.
        'group flex items-center gap-2 rounded-md px-2 transition-colors',
        clickable &&
          'cursor-pointer hover:bg-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'
      )}
      style={{ minHeight: rem(32), minWidth: 0 }}
    >
      {lead && <span className="flex-shrink-0">{lead}</span>}
      <span className="min-w-0 flex-1 truncate text-sm text-text">{primary}</span>
      {meta && <span className="flex-shrink-0 text-sm text-muted">{meta}</span>}
      {trailing &&
        (trailingReveal ? (
          <span className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 focus-within:opacity-100">
            {trailing}
          </span>
        ) : (
          trailing
        ))}
    </div>
  );
}

export function WList({
  rows,
  groups,
  max = 8,
  minColRem = LIST_MIN_COL_REM,
  onViewAll,
  footer,
}: WListProps) {
  if (groups) {
    return (
      <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(auto-fill, minmax(${minColRem}rem, 1fr))`,
            gap: 'var(--space-3) var(--space-4)',
            alignItems: 'start',
          }}
        >
          {groups.map((g) => (
            <div
              key={g.key}
              className="flex flex-col"
              style={{ gap: 'var(--space-1)', minWidth: 0 }}
            >
              {g.label}
              <div className="flex flex-col" style={{ gap: 'var(--space-1)' }}>
                {g.rows.map((row) => (
                  <WListRowItem key={row.id} {...row} />
                ))}
              </div>
            </div>
          ))}
        </div>
        {footer}
      </div>
    );
  }

  const visible = sliceRows(rows ?? [], max);
  const truncated = (rows ?? []).length > visible.length;
  return (
    <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${minColRem}rem, 1fr))`,
          gap: 'var(--space-1) var(--space-4)',
        }}
      >
        {visible.map((row) => (
          <WListRowItem key={row.id} {...row} />
        ))}
      </div>
      {footer}
      {onViewAll && truncated && (
        <button
          type="button"
          onClick={onViewAll}
          className="self-start flex-shrink-0 text-xs font-semibold text-muted transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          style={{ minHeight: rem(24) }}
        >
          {viewAllLabel((rows ?? []).length)}
        </button>
      )}
    </div>
  );
}
